import * as customerPortalApi from '@kk/api/modules/customerPortal';
import { capitalizeFirstLetter } from '@kk/shared/utils/strings';
import InputCombobox from '@kk/ui/components/InputCombobox';
import InputFiscalAmount from '@kk/ui/components/InputFiscalAmount';
import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

export enum FieldNames {
  PrincipalAmount = 'principalAmount',
  MaturityInYears = 'maturityInYears',
  LoanProduct = 'loanProduct',
  AmortizationProfile = 'amortizationProfile',
  FixedInterestPeriod = 'fixedInterestPeriod',
  FixedInterestYears = 'fixedInterestYears',
  InterestPaymentFrequency = 'interestPaymentFrequency',
}

export function PrincipalAmount() {
  const { t } = useTranslation(['ui']);
  const loanProduct = useWatch({ name: FieldNames.LoanProduct });

  return (
    <InputFiscalAmount
      name={FieldNames.PrincipalAmount}
      label={
        loanProduct === customerPortalApi.ContractLoanTypeEnum.Byggekredit
          ? t('loan.principalAmount.credit')
          : t('loan.principalAmount')
      }
      labelDescription={t('calculatorForm.principalAmount.description')}
    />
  );
}

export function MaturityInYears() {
  const { t } = useTranslation(['ui']);
  const { setValue } = useFormContext();
  const loanProduct = useWatch({ name: FieldNames.LoanProduct });
  const maxYears =
    loanProduct === customerPortalApi.ContractLoanTypeEnum.Byggekredit ? 5 : 40;

  return (
    <InputCombobox
      name={FieldNames.MaturityInYears}
      label={capitalizeFirstLetter(t('calculatorForm.loanPeriod.label'))}
      labelDescription={t('calculatorForm.loanPeriod.description')}
      placeholder={t('calculatorForm.loanPeriod.placeholder')}
      options={Array.from({ length: maxYears }).map((_, i) => ({
        label: `${i + 1} ${t('year', { count: i + 1 })}`,
        value: String(i + 1),
      }))}
      optionsClassName="max-h-[368px] overflow-y-auto"
      onChange={(option) => {
        if (loanProduct !== customerPortalApi.ContractLoanTypeEnum.KKfast)
          return;
        const period = Number(option?.value);
        const defaultYears = period < 30 ? period : 30;
        setValue(FieldNames.FixedInterestPeriod, String(defaultYears), {
          shouldValidate: true,
        });
        setValue(FieldNames.FixedInterestYears, String(defaultYears), {
          shouldValidate: true,
        });
      }}
    />
  );
}

export function ProductType() {
  const { t } = useTranslation(['ui']);
  const { setValue, getValues, resetField } = useFormContext();

  const loanPeriod = useWatch({ name: FieldNames.MaturityInYears });

  const handleLoanProductChange = useCallback(
    (loanProduct: customerPortalApi.ContractLoanTypeEnum | undefined) => {
      if (!loanProduct) throw new Error('missing loan product');
      function resetInterestPaymentFrequency() {
        if (
          getValues(FieldNames.InterestPaymentFrequency) ===
          customerPortalApi.PaymentFreqEnum.UnDefined
        ) {
          resetField(FieldNames.InterestPaymentFrequency);
        }
      }

      match(loanProduct)
        .with(customerPortalApi.ContractLoanTypeEnum.KKvar, () => {
          setValue(
            FieldNames.AmortizationProfile,
            customerPortalApi.AmortizationProfile.Serial,
          );
          setValue(
            FieldNames.FixedInterestPeriod,
            customerPortalApi.FixedInterestPeriodType.UnDefined,
          );
          resetInterestPaymentFrequency();
        })
        .with(customerPortalApi.ContractLoanTypeEnum.KKcibor, () => {
          setValue(
            FieldNames.AmortizationProfile,
            customerPortalApi.AmortizationProfile.Serial,
          );
          setValue(
            FieldNames.FixedInterestPeriod,
            customerPortalApi.FixedInterestPeriodType.Cheapest,
          );
          setValue(FieldNames.FixedInterestYears, undefined);
          resetInterestPaymentFrequency();
        })
        .with(customerPortalApi.ContractLoanTypeEnum.KKfast, () => {
          setValue(
            FieldNames.AmortizationProfile,
            customerPortalApi.AmortizationProfile.Annuity,
          );
          const period = Number(loanPeriod);
          if (!loanPeriod) return;
          const defaultYears = period < 30 ? period : 30;
          setValue(FieldNames.FixedInterestPeriod, String(defaultYears), {
            shouldValidate: true,
          });
          setValue(FieldNames.FixedInterestYears, String(defaultYears), {
            shouldValidate: true,
          });
          resetInterestPaymentFrequency();
        })
        .with(customerPortalApi.ContractLoanTypeEnum.Byggekredit, () => {
          setValue(
            FieldNames.AmortizationProfile,
            customerPortalApi.AmortizationProfile.Bullet,
            { shouldValidate: true },
          );
          setValue(
            FieldNames.FixedInterestPeriod,
            customerPortalApi.FixedInterestPeriodType.UnDefined,
            { shouldValidate: true },
          );
          setValue(
            FieldNames.InterestPaymentFrequency,
            customerPortalApi.PaymentFreqEnum.UnDefined,
            { shouldValidate: true },
          );
          // Reset maturity period if it exceeds Byggekredit maximum (5 years)
          const currentMaturity = Number(loanPeriod);
          if (currentMaturity > 5) {
            setValue(FieldNames.MaturityInYears, undefined);
          }
        });
    },
    [setValue, loanPeriod, getValues, resetField],
  );

  return (
    <InputCombobox
      name={FieldNames.LoanProduct}
      label={capitalizeFirstLetter(t('calculatorForm.loanProduct.label'))}
      placeholder={t('calculatorForm.loanProduct.placeholder')}
      labelDescription={t('calculatorForm.loanProduct.description')}
      options={[
        {
          value: customerPortalApi.ContractLoanTypeEnum.KKfast,
          label: t('loanProduct.KKfast'),
          description: t('loanProduct.KKfast.description'),
        },
        {
          value: customerPortalApi.ContractLoanTypeEnum.KKvar,
          label: t('loanProduct.KKvar'),
          description: t('loanProduct.KKvar.description'),
        },
        {
          value: customerPortalApi.ContractLoanTypeEnum.KKcibor,
          label: t('loanProduct.KKcibor'),
          description: t('loanProduct.KKcibor.description'),
        },
        {
          value: customerPortalApi.ContractLoanTypeEnum.Byggekredit,
          label: t('loanProduct.Byggekredit'),
          description: t('loanProduct.Byggekredit.description'),
        },
      ]}
      onChange={(option) => handleLoanProductChange(option?.value)}
    />
  );
}

export function AmortizationProfile() {
  const { t } = useTranslation(['ui']);
  const loanProduct = useWatch({ name: FieldNames.LoanProduct });

  return (
    <InputCombobox
      name={FieldNames.AmortizationProfile}
      label={capitalizeFirstLetter(
        t('calculatorForm.amortizationProfile.label'),
      )}
      disabled={[
        customerPortalApi.ContractLoanTypeEnum.KKvar,
        customerPortalApi.ContractLoanTypeEnum.Byggekredit,
        customerPortalApi.ContractLoanTypeEnum.KKcibor,
      ].includes(loanProduct)}
      placeholder={t('calculatorForm.amortizationProfile.placeholder')}
      labelDescription={t('calculatorForm.amortizationProfile.description')}
      options={
        loanProduct === customerPortalApi.ContractLoanTypeEnum.Byggekredit
          ? [
              {
                value: customerPortalApi.AmortizationProfile.Bullet,
                label: t('amortizationProfile.Bullet'),
              },
            ]
          : [
              {
                value: customerPortalApi.AmortizationProfile.Annuity,
                label: t('amortizationProfile.Annuity'),
                description: t('amortizationProfile.Annuity.description'),
              },
              {
                value: customerPortalApi.AmortizationProfile.Serial,
                label: t('amortizationProfile.Serial'),
                description: t('amortizationProfile.Serial.description'),
              },
            ]
      }
    />
  );
}

export function FixedInterestPeriod() {
  const { t } = useTranslation(['ui']);
  const { setValue, resetField, register } = useFormContext();
  const loanProduct = useWatch({ name: FieldNames.LoanProduct });
  const loanPeriod = useWatch({ name: FieldNames.MaturityInYears });
  const hiddenFieldRegistration = register(FieldNames.FixedInterestYears);

  return (
    <>
      <input type="hidden" {...hiddenFieldRegistration} />
      <InputCombobox
        name={FieldNames.FixedInterestPeriod}
        label={capitalizeFirstLetter(
          t('calculatorForm.fixedInterestPeriod.label'),
        )}
        placeholder={t('calculatorForm.fixedInterestPeriod.placeholder')}
        disabled={[
          customerPortalApi.ContractLoanTypeEnum.KKvar,
          customerPortalApi.ContractLoanTypeEnum.Byggekredit,
        ].includes(loanProduct)}
        labelDescription={t('calculatorForm.fixedInterestPeriod.description')}
        optionsMaxHeight={368}
        options={
          [
            customerPortalApi.ContractLoanTypeEnum.KKvar,
            customerPortalApi.ContractLoanTypeEnum.Byggekredit,
          ].includes(loanProduct)
            ? [
                {
                  label: t('notRelevant'),
                  value: customerPortalApi.FixedInterestPeriodType.UnDefined,
                },
              ]
            : [
                {
                  label: t('fixedInterestPeriod.ToMaturity'),
                  value: customerPortalApi.FixedInterestPeriodType.ToMaturity,
                  disabled: Number(loanPeriod) > 30,
                },
                {
                  label: t('fixedInterestPeriod.Cheapest'),
                  value: customerPortalApi.FixedInterestPeriodType.Cheapest,
                },
                ...Array.from({ length: 30 }).map((_, i) => ({
                  label: `${i + 1} ${t('year', { count: i + 1 })}`,
                  value: String(i + 1),
                  disabled: i + 1 > Number(loanPeriod),
                })),
              ]
        }
        onChange={(option) => {
          if (!option) return;
          if (
            [
              customerPortalApi.FixedInterestPeriodType.Cheapest,
              customerPortalApi.FixedInterestPeriodType.ToMaturity,
            ].includes(
              option.value as customerPortalApi.FixedInterestPeriodType,
            )
          ) {
            resetField(FieldNames.FixedInterestYears);
          } else {
            setValue(FieldNames.FixedInterestYears, option.value);
          }
        }}
      />
    </>
  );
}

export function RateFrequency() {
  const { t } = useTranslation(['ui']);
  const loanProduct = useWatch({ name: FieldNames.LoanProduct });

  return (
    <InputCombobox
      name={FieldNames.InterestPaymentFrequency}
      label={capitalizeFirstLetter(t('calculatorForm.rateFrequency.label'))}
      placeholder={t('calculatorForm.rateFrequency.placeholder')}
      labelDescription={t('calculatorForm.rateFrequency.description')}
      disabled={
        loanProduct === customerPortalApi.ContractLoanTypeEnum.Byggekredit
      }
      options={
        customerPortalApi.ContractLoanTypeEnum.Byggekredit === loanProduct
          ? [
              {
                label: t('notRelevant'),
                value: customerPortalApi.PaymentFreqEnum.UnDefined,
              },
            ]
          : [
              {
                value: customerPortalApi.PaymentFreqEnum.M6,
                label: t('rateFrequency.M6'),
              },
              {
                value: customerPortalApi.PaymentFreqEnum.M3,
                label: t('rateFrequency.M3'),
              },
            ]
      }
    />
  );
}
